import { Box } from '@abyss/web/ui/Box';
import { V2Footer } from '@abyss/web/ui/Footer';
import { SubFooterLink } from '@abyss/web/ui/Footer/V2Footer.types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLagoon } from '../../hooks/useLagoon';

export const ProviderDirectoryFooter = () => {
  const configuration = useLagoon('provider-directory-footer-configuration')();
  const { t } = useTranslation();

  const getFooterData = () => {
    // Group links by column and footer type
    const columnMap = new Map<string, any>();

    configuration?.forEach((link) => {
      const key = `${link.columnPosition}-${link.footerType}`;

      if (!columnMap.has(key)) {
        columnMap.set(key, {
          header: link.columnHeaderName,
          position: Number(link.columnPosition),
          footerType: link.footerType,
          links: [],
        });
      }

      const column = columnMap.get(key)!;
      column.links.push({
        name: link.linkName,
        url: link.linkUrl,
        position: Number(link.linkPosition),
      });
    });

    // Sort links within each column and sort columns by position
    const allColumns = Array.from(columnMap.values())
      .map((column) => ({
        ...column,
        links: column.links.sort((a, b) => a.position - b.position),
      }))
      .sort((a, b) => a.position - b.position);

    return {
      section: allColumns.filter((col) => col.footerType === t('SECTION')),
      general: allColumns.filter((col) => col.footerType === t('GENERAL')),
    };
  };

  const footerData = useMemo(() => getFooterData(), [configuration]);

  const section = footerData?.section || [];
  const subFooterLinks: SubFooterLink[] = useMemo(
    () =>
      footerData?.general?.[0]?.links.map((link) => ({
        label: link?.name,
        href: link?.url || '#',
        hideIcon: true,
      })) || [],
    [footerData]
  );

  return (
    <Box
      css={{
        'abyss-box-root': {
          backgroundColor: '$white',
          width: '100%',
          opacity: 1,
          fontFamily: '$primary',
          marginTop: 'auto',
          padding: '0px',
        },
      }}
    >
      <V2Footer
        css={{
          'abyss-footer-copyright': {
            display: 'none',
          },
          'abyss-footer-sub-footnotes': {
            justifyContent: 'flex-start',
            display: 'block',
          },
          'abyss-footer-sub-link': {
            textDecoration: 'none',
          },
          'abyss-footer-link abyss-link-icon-wrapper': {
            display: 'none !important',
          },
        }}
        data-testid="provider-directory-footer"
        disableDefaultProviderProps={true}
        fullWidth
        subFooter={{
          links: subFooterLinks,
        }}
      >
        {section?.length > 0 &&
          section.map((column, i) => (
            <V2Footer.Section
              css={{
                'abyss-footer-section-title': {
                  fontSize: '$xl !important',
                },
                'abyss-footer-link-item': {
                  textDecoration: 'none',
                },
              }}
              data-testid="footer-heading"
              key={`footer-column-${i}`}
              title={column?.header}
            >
              {column.links.map((link, j) => (
                <V2Footer.Link
                  css={{
                    'abyss-footer-link': {
                      textDecoration: 'none',
                    },
                  }}
                  data-testid="footer-links"
                  hideIcon={true}
                  href={link.url}
                  key={`footer-link-${i}-${j}`}
                >
                  {link.name}
                </V2Footer.Link>
              ))}
            </V2Footer.Section>
          ))}
      </V2Footer>
    </Box>
  );
};

export default ProviderDirectoryFooter;
